import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Search, Clock, Truck, ShoppingBag, Store as StoreIcon, LocateFixed, ArrowRight, Tag, Navigation, Heart, Map as MapIcon, SlidersHorizontal, Sandwich, Beef, Pizza, Utensils, Coffee, Cake, IceCream, Fish, Drumstick, ChefHat, Pill, HeartPulse, Stethoscope, BadgePercent, Sparkles } from "lucide-react";
import { PortalNav } from "../components/PortalNav";
import { StoreMap } from "../components/StoreMap";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const CATEGORIES = ["Panini", "Hamburger", "Pizzerie", "Ristoranti", "Piadinerie", "Bar e colazioni", "Pasticcerie", "Gelaterie", "Sushi", "Pollo e rosticceria", "Cucina locale", "Farmacie", "Parafarmacie", "Sanitarie", "Altro"];
const CAT_ICONS = {
  "Panini": Sandwich, "Hamburger": Beef, "Pizzerie": Pizza, "Ristoranti": Utensils,
  "Piadinerie": ChefHat, "Bar e colazioni": Coffee, "Pasticcerie": Cake, "Gelaterie": IceCream,
  "Sushi": Fish, "Pollo e rosticceria": Drumstick, "Cucina locale": ChefHat,
  "Farmacie": Pill, "Parafarmacie": HeartPulse, "Sanitarie": Stethoscope, "Altro": StoreIcon,
};
const STEPS = [
  { icon: StoreIcon, t: "Scegli il locale", d: "Trova le attività della tua città e sfoglia il menu." },
  { icon: ShoppingBag, t: "Ordina e paga online", d: "Componi l'ordine, applica i coupon e paga in sicurezza." },
  { icon: Clock, t: "Ritira senza attese", d: "Ricevi la conferma e ritiri appena è pronto." },
];
const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const HERO_IMG = "https://images.unsplash.com/photo-1652862730784-bb2a6e862514?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const FOOD_FALLBACKS = [
  "https://images.unsplash.com/photo-1652862729869-2f4e80c1849d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.unsplash.com/photo-1651440204227-a9a5b9d19712?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  "https://images.pexels.com/photos/16688015/pexels-photo-16688015.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
  "https://images.unsplash.com/photo-1652862730784-bb2a6e862514?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
];
function fallbackImg(slug) {
  let h = 0; const s = slug || "";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return FOOD_FALLBACKS[h % FOOD_FALLBACKS.length];
}

function couponLabel(c) {
  return c.type === "percentage" ? `-${c.value}%` : c.type === "fixed" ? `-€${Number(c.value).toFixed(0)}` : "Consegna gratis";
}

const parseLatLng = (s) => {
  if (!s || typeof s !== "string" || !s.includes(",")) return undefined;
  const parts = s.split(",").map((x) => parseFloat(x.trim()));
  if (parts.length !== 2 || !parts.every(Number.isFinite)) return undefined;
  const [lat, lng] = parts;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return undefined;
  return [lat, lng];
};

function SearchBar({ initLoc = "", initQ = "", big }) {
  const nav = useNavigate();
  const [term, setTerm] = useState(initQ || initLoc);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [history, setHistory] = useState(() => { try { return JSON.parse(localStorage.getItem("pf_search_history") || "[]"); } catch { return []; } });
  const pushHistory = (val) => {
    const v = (val || "").trim();
    if (!v) return;
    const next = [v, ...history.filter((h) => h.toLowerCase() !== v.toLowerCase())].slice(0, 6);
    setHistory(next);
    try { localStorage.setItem("pf_search_history", JSON.stringify(next)); } catch (e) { /* storage off */ }
  };
  const boxRef = useRef(null);

  // Magic Box: cerca tutto (cibo/negozio/via/indirizzo). Locali+prodotti da /portal/suggest,
  // luoghi da /portal/geo-suggest (OpenStreetMap). Google Places verrà agganciato qui.
  useEffect(() => {
    const v = term.trim();
    if (v.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const [s, g] = await Promise.all([
        api.get("/portal/suggest", { params: { q: v } }).then((r) => r.data).catch(() => []),
        api.get("/portal/geo-suggest", { params: { q: v } }).then((r) => r.data).catch(() => []),
      ]);
      const stores = (s || []).map((x) => ({ ...x, kind: x.type || "store" }));
      const places = (g || []).map((x) => ({ ...x, kind: "place" }));
      setResults([...stores.slice(0, 5), ...places.slice(0, 4)]);
    }, 250);
    return () => clearTimeout(t);
  }, [term]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (item) => {
    setOpen(false);
    pushHistory(item.label);
    if (item.kind === "place") { nav(`/esplora?loc=${encodeURIComponent(item.label)}${term.trim() ? `&q=${encodeURIComponent(term.trim())}` : ""}`); return; }
    nav(`/locale/${item.tenant_slug}/${item.store_slug}`);
  };
  const go = () => { const v = term.trim(); if (v) pushHistory(v); setOpen(false); nav(`/esplora?q=${encodeURIComponent(v)}`); };
  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { nav(`/esplora?loc=${p.coords.latitude.toFixed(4)},${p.coords.longitude.toFixed(4)}`); setGeoBusy(false); },
      () => setGeoBusy(false), { timeout: 8000 });
  };

  const iconFor = (k) => (k === "place" ? MapPin : k === "product" ? Utensils : StoreIcon);

  return (
    <div className={`bg-white rounded-3xl shadow-2xl border border-black/5 ${big ? "p-2.5 md:p-3" : "p-2"}`} data-testid="ch-searchbar">
      <div className="flex flex-col sm:flex-row gap-2.5">
        <div className="flex-1 rounded-2xl bg-neutral-50 sm:bg-transparent px-4 py-2.5 transition-colors focus-within:bg-neutral-100 relative" ref={boxRef}>
          <label className="flex items-center gap-1.5 text-[11px] font-bold text-[#c79400] uppercase tracking-wide">
            <Sparkles size={12} /> Magic Box
          </label>
          <div className="flex items-center gap-2.5">
            <Search size={20} className="text-neutral-700 shrink-0" />
            <input value={term} onChange={(e) => { setTerm(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
              onKeyDown={(e) => e.key === "Enter" && go()} placeholder="Cerca cibo, negozio, via o indirizzo…"
              className="w-full bg-transparent outline-none text-base md:text-lg py-1 text-neutral-900 placeholder:text-neutral-400" data-testid="ch-magic-input" autoComplete="off" />
          </div>
          {open && results.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-2xl border border-black/10 bg-white shadow-2xl overflow-hidden" data-testid="ch-magic-suggestions">
              {results.map((s, idx) => {
                const Ic = iconFor(s.kind);
                return (
                  <button key={idx} type="button" onMouseDown={() => pick(s)} data-testid={`ch-magic-suggest-${idx}`}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-neutral-100 transition-colors">
                    <span className="w-8 h-8 rounded-lg bg-[#FFC400]/15 grid place-items-center text-[#c79400] shrink-0"><Ic size={15} /></span>
                    <span className="min-w-0">
                      <span className="block text-sm text-neutral-900 truncate">{s.label}</span>
                      <span className="block text-xs text-neutral-500 truncate">{s.kind === "place" ? "Luogo" : s.kind === "product" ? (s.sublabel || "Prodotto") : "Locale · " + (s.sublabel || "")}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {open && !term.trim() && history.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-2xl border border-black/10 bg-white shadow-2xl overflow-hidden p-3" data-testid="ch-search-history">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold text-neutral-500 uppercase tracking-wide">Ricerche recenti</span>
                <button type="button" onMouseDown={() => { setHistory([]); try { localStorage.removeItem("pf_search_history"); } catch (e) { /* noop */ } }} className="text-[11px] text-neutral-400 hover:text-neutral-700" data-testid="ch-history-clear">Cancella</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {history.map((h, idx) => (
                  <button key={idx} type="button" onMouseDown={() => { setTerm(h); setOpen(false); pushHistory(h); nav(`/esplora?q=${encodeURIComponent(h)}`); }} data-testid={`ch-history-${idx}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200 px-3 py-1.5 text-sm text-neutral-800 transition-colors">
                    <Clock size={13} className="text-neutral-400" /> {h}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <Button onClick={go} className="w-full sm:w-auto rounded-2xl bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] font-bold px-7 h-14 sm:h-auto text-base shadow-lg hover:scale-[1.02] active:scale-95 transition-all duration-200" data-testid="ch-search-btn"><Search size={18} className="mr-1.5" />Cerca</Button>
      </div>
      <button onClick={useMyLocation} className="mt-2 ml-1 inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-[#c79400] transition-colors" data-testid="ch-use-location">
        <LocateFixed size={13} /> {geoBusy ? "Rilevo la posizione…" : "Usa la mia posizione"}
      </button>
    </div>
  );
}

function StoreCard({ s, i, fav, onToggleFav, coupon }) {
  return (
    <motion.div {...(reduce ? {} : { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.4, delay: (i % 6) * 0.06 } })}>
      <Link to={`/locale/${s.tenant_slug}/${s.store_slug}`} data-testid={`ch-store-${s.store_slug}`}
        className="group block bg-white/[0.04] rounded-2xl border border-white/10 hover:border-[#FFC400]/50 hover:-translate-y-1 transition-all overflow-hidden">
        <div className="h-28 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${s.color || "#FFC400"}, #0B0B0D)` }}>
          {s.logo ? (
            <div className="absolute inset-0 grid place-items-center bg-white/5"><img src={s.logo} alt={s.name} className="max-h-16 max-w-[70%] object-contain drop-shadow" loading="lazy" /></div>
          ) : (
            <img src={s.cover || fallbackImg(s.store_slug)} alt={s.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
          {onToggleFav && (
            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(s); }} data-testid={`ch-fav-${s.store_slug}`}
              className="absolute top-2 left-2 w-8 h-8 grid place-items-center rounded-full bg-black/40 backdrop-blur hover:bg-black/60 transition-colors z-10">
              <Heart size={15} className={fav ? "fill-[#FFC400] text-[#FFC400]" : "text-white"} />
            </button>
          )}
          {coupon && (
            <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[11px] font-extrabold bg-[#FFC400] text-[#0B0B0D] px-2 py-0.5 rounded-full shadow z-10" data-testid={`ch-card-coupon-${s.store_slug}`}>
              <Tag size={11} /> {couponLabel(coupon)}
            </span>
          )}
          {s.distance_km != null && (
            <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] font-bold bg-black/50 text-white px-2 py-0.5 rounded-full backdrop-blur z-10">
              <Navigation size={11} /> {s.distance_km} km
            </span>
          )}
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between gap-2">
            <p className="font-head font-bold text-white">{s.name}</p>
            {s.demo && <span className="text-[9px] font-bold bg-amber-400/20 text-amber-300 px-1.5 py-0.5 rounded">DEMO</span>}
          </div>
          <p className="text-xs text-white/50 truncate">{s.brand} · {s.address}</p>
          <div className="flex items-center flex-wrap gap-2 mt-2 text-xs">
            <span className={`px-2 py-0.5 rounded-full font-semibold ${s.open ? "bg-green-500/20 text-green-300" : "bg-white/10 text-white/50"}`}>{s.open ? "Aperto" : "Chiuso"}</span>
            <span className="inline-flex items-center gap-1 text-white/50"><Clock size={12} /> {s.prep_time}′</span>
            <span className="inline-flex items-center gap-1 text-white/50"><ShoppingBag size={12} /> Ritiro</span>
            {s.delivery && <span className="inline-flex items-center gap-1 text-[#FFC400]"><Truck size={12} /> Delivery</span>}
            {!s.min_order && <span className="inline-flex items-center gap-1 font-semibold bg-[#FFC400]/15 text-[#FFC400] px-2 py-0.5 rounded-full" data-testid={`ch-nomin-${s.store_slug}`}><BadgePercent size={12} /> Senza minimo</span>}
          </div>
          {coupon && (
            <div className="mt-2 flex items-center gap-2 rounded-xl border border-dashed border-[#FFC400]/40 bg-[#FFC400]/[0.06] px-3 py-2">
              <Tag size={14} className="text-[#FFC400] shrink-0" />
              <span className="text-xs font-semibold text-white">{couponLabel(coupon)} · <span className="text-[#FFC400]">{coupon.code}</span></span>
              {coupon.min_order > 0 && <span className="text-[11px] text-white/50 ml-auto">min €{Number(coupon.min_order).toFixed(0)}</span>}
            </div>
          )}
          <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#FFC400] group-hover:gap-2 transition-all">Vedi menu <ArrowRight size={14} /></div>
        </div>
      </Link>
    </motion.div>
  );
}

function CouponStrip() { return null; }


export default function CustomerHome({ mode = "home" }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isCustomer = user && user.role === "customer";
  const [stores, setStores] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [openOnly, setOpenOnly] = useState(false);
  const [deliveryOnly, setDeliveryOnly] = useState(false);
  const [maxDist, setMaxDist] = useState(0);
  const [typeFilter, setTypeFilter] = useState("");
  const [showMap, setShowMap] = useState(true);
  const [geoLoc, setGeoLoc] = useState("");
  const [geoBusy, setGeoBusy] = useState(false);
  const urlLoc = params.get("loc") || "";
  const q = params.get("q") || "";
  const loc = urlLoc || geoLoc;

  const locateMe = () => {
    if (!navigator.geolocation) { toast.error("Geolocalizzazione non disponibile"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setGeoLoc(`${p.coords.latitude.toFixed(5)},${p.coords.longitude.toFixed(5)}`); setGeoBusy(false); },
      () => { setGeoBusy(false); toast.error("Non riesco a rilevare la posizione"); },
      { timeout: 8000, enableHighAccuracy: true });
  };

  useEffect(() => {
    if (mode === "home" && !urlLoc && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setGeoLoc(`${p.coords.latitude.toFixed(5)},${p.coords.longitude.toFixed(5)}`),
        () => {}, { timeout: 8000 });
    }
  }, [mode, urlLoc]);

  useEffect(() => {
    setLoading(true); setErr(null);
    api.get("/portal/stores", { params: { loc, q } })
      .then((r) => setStores(r.data)).catch((e) => setErr(apiErr(e))).finally(() => setLoading(false));
    api.get("/portal/coupons", { params: { loc, q } }).then((r) => setCoupons(r.data)).catch(() => {});
  }, [loc, q]);

  useEffect(() => {
    if (!isCustomer) { setFavorites([]); return; }
    api.get("/account/favorites").then((r) => setFavorites(r.data)).catch(() => {});
  }, [isCustomer]);

  const favSet = new Set(favorites.map((f) => `${f.tenant_slug}/${f.store_slug}`));

  const toggleFav = async (s) => {
    if (!isCustomer) { nav("/account/login"); return; }
    const key = `${s.tenant_slug}/${s.store_slug}`;
    const has = favSet.has(key);
    setFavorites((prev) => has
      ? prev.filter((f) => `${f.tenant_slug}/${f.store_slug}` !== key)
      : [...prev, { tenant_slug: s.tenant_slug, store_slug: s.store_slug, name: s.name, brand: s.brand, address: s.address, open: s.open, prep_time: s.prep_time, delivery: s.delivery, color: s.color }]);
    try {
      if (has) await api.delete(`/account/favorites/${s.tenant_slug}/${s.store_slug}`);
      else { await api.post("/account/favorites", { tenant_slug: s.tenant_slug, store_slug: s.store_slug }); toast.success("Aggiunto ai preferiti"); }
    } catch (e) { toast.error(apiErr(e)); }
  };

  const anim = reduce ? {} : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
  const hasDist = stores.some((s) => s.distance_km != null);
  const couponMap = coupons.reduce((acc, c) => { const k = `${c.tenant_slug}/${c.store_slug}`; if (!acc[k]) acc[k] = c; return acc; }, {});
  const filtered = stores.filter((s) => (!openOnly || s.open) && (!deliveryOnly || s.delivery) && (!maxDist || (s.distance_km != null && s.distance_km <= maxDist)) && (!typeFilter || (s.business_type || "food") === typeFilter));

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <PortalNav />

      {mode === "home" && isCustomer && favorites.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pt-6" data-testid="ch-fav-row">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-head text-xl font-bold flex items-center gap-2"><Heart size={18} className="fill-[#FFC400] text-[#FFC400]" /> I tuoi preferiti</h2>
            <Link to="/account" className="text-sm font-semibold text-[#FFC400] hover:underline" data-testid="ch-fav-manage">Gestisci →</Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
            {favorites.map((f, i) => (
              <div key={`${f.tenant_slug}-${f.store_slug}`} className="w-72 shrink-0">
                <StoreCard s={f} i={i} fav={true} onToggleFav={toggleFav} />
              </div>
            ))}
          </div>
        </section>
      )}

      {mode === "home" && (
        <>
          <section className="relative overflow-hidden">
            {!reduce && (
              <>
                <motion.div aria-hidden className="pointer-events-none absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#FFC400]/20 blur-3xl"
                  animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }} transition={{ duration: 8, repeat: Infinity }} />
                <motion.div aria-hidden className="pointer-events-none absolute top-40 -left-32 w-[24rem] h-[24rem] rounded-full bg-indigo-500/10 blur-3xl"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 10, repeat: Infinity }} />
              </>
            )}
            <div className="max-w-6xl mx-auto px-4 pt-8 pb-6 md:pt-14 md:pb-8 relative">
              <div>
                <motion.h1 {...anim} className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">Ordina. Ritira. <span className="text-[#FFC400]">Gusta.</span></motion.h1>
                <motion.p {...anim} className="mt-3 text-base md:text-lg text-white/60 max-w-xl">Con Pickfood trovi i negozi della tua città, ordini online e ritiri senza attese.</motion.p>
                <motion.div {...(reduce ? {} : { initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.1 } })}
                  className="mt-6 hidden sm:block relative rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl" data-testid="ch-hero-visual">
                  <img src={HERO_IMG} alt="Street food pronto da ritirare" className="w-full h-40 md:h-52 object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D]/70 via-transparent to-transparent" />
                </motion.div>
              </div>

              <motion.div {...(reduce ? {} : { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.55, delay: 0.15 } })} className="mt-8 max-w-3xl relative z-10">
                <SearchBar big />
              </motion.div>
            </div>
          </section>

          <section className="max-w-6xl mx-auto px-4 pt-5 pb-1" data-testid="ch-categories">
            <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
              {CATEGORIES.map((c, i) => {
                const Icon = CAT_ICONS[c] || StoreIcon;
                return (
                  <motion.button key={c} onClick={() => nav(`/esplora?q=${encodeURIComponent(c)}`)} data-testid={`ch-cat-${c}`}
                    {...(reduce ? {} : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.03 } })}
                    className="whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/80 hover:border-[#FFC400] hover:text-[#FFC400] transition-colors">
                    <span className="inline-flex items-center gap-1.5"><Icon size={14} className="text-[#FFC400]" /> {c}</span>
                  </motion.button>
                );
              })}
            </div>
          </section>

          <section className="max-w-6xl mx-auto px-4 py-8" data-testid="ch-nearby-carousel">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h2 className="font-head text-2xl font-bold">Attività vicino a te</h2>
              <Link to="/esplora" className="text-sm font-semibold text-[#FFC400] hover:underline shrink-0" data-testid="ch-nearby-all">Vedi tutte →</Link>
            </div>
            {loading && <p className="text-white/50" data-testid="ch-nearby-loading">Caricamento attività…</p>}
            {!loading && filtered.length === 0 && (
              <div className="bg-white/[0.04] rounded-2xl border border-white/10 p-8 text-center" data-testid="ch-nearby-empty">
                <p className="font-semibold">Nessuna attività trovata</p>
                <p className="text-sm text-white/50 mt-1">Attiva la posizione o cerca una città per scoprire i negozi vicino a te.</p>
              </div>
            )}
            {!loading && filtered.length > 0 && (
              <div className="flex gap-4 overflow-x-auto pb-3 no-scrollbar snap-x snap-mandatory" data-testid="ch-nearby-track">
                {filtered.map((s, i) => (
                  <div key={`${s.tenant_slug}-${s.store_slug}`} className="w-72 shrink-0 snap-start">
                    <StoreCard s={s} i={i} fav={favSet.has(`${s.tenant_slug}/${s.store_slug}`)} onToggleFav={toggleFav} coupon={couponMap[`${s.tenant_slug}/${s.store_slug}`]} />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="max-w-6xl mx-auto px-4 pb-4" data-testid="ch-home-map-wrap">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h2 className="font-head text-lg font-bold flex items-center gap-2"><MapIcon size={18} className="text-[#FFC400]" /> Mappa attività vicino a te</h2>
              <button onClick={locateMe} data-testid="ch-locate-me"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#FFC400]/40 bg-[#FFC400]/10 px-3.5 py-1.5 text-sm font-semibold text-[#FFC400] hover:bg-[#FFC400]/20 transition-colors">
                <LocateFixed size={14} /> {geoBusy ? "Rilevo la posizione…" : "Usa la mia posizione"}
              </button>
            </div>
            {(function () {
              const coord = loc.includes(",") && !isNaN(parseFloat(loc)) ? parseLatLng(loc) : undefined;
              const nearStores = coord ? filtered.filter((s) => s.lat != null && (s.distance_km == null || s.distance_km <= 50)) : filtered.filter((s) => s.lat != null);
              if (coord || nearStores.length) {
                return <StoreMap stores={nearStores} center={coord} userLoc={coord} />;
              }
              return (
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center text-sm text-white/55" data-testid="ch-home-map-empty">
                  {loading ? "Carico la mappa delle attività…" : "Tocca “Usa la mia posizione” o cerca una città per vedere la mappa."}
                </div>
              );
            })()}
          </section>

        </>
      )}

      {mode !== "home" && (
      <section className="max-w-6xl mx-auto px-4 pb-10 pt-2">
        <h2 className="font-head text-2xl font-bold mb-4">Risultati della ricerca</h2>
        <>
            <div className="mb-4 max-w-3xl"><SearchBar initLoc={loc} initQ={q} /></div>
            <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="ch-filters">
              <span className="inline-flex items-center gap-1.5 text-xs text-white/50 mr-1"><SlidersHorizontal size={13} /> Filtri</span>
              <button onClick={() => setOpenOnly((v) => !v)} data-testid="filter-open"
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors ${openOnly ? "bg-[#FFC400] border-[#FFC400] text-[#0B0B0D]" : "bg-white/5 border-white/10 text-white/80 hover:border-[#FFC400]"}`}>Aperto ora</button>
              <button onClick={() => setDeliveryOnly((v) => !v)} data-testid="filter-delivery"
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors ${deliveryOnly ? "bg-[#FFC400] border-[#FFC400] text-[#0B0B0D]" : "bg-white/5 border-white/10 text-white/80 hover:border-[#FFC400]"}`}>Delivery</button>
              <select value={q} onChange={(e) => nav(`/esplora?loc=${encodeURIComponent(loc)}&q=${encodeURIComponent(e.target.value)}`)} data-testid="filter-cuisine"
                className="rounded-full px-3.5 py-1.5 text-sm font-medium bg-white/5 border border-white/10 text-white/80 outline-none hover:border-[#FFC400] cursor-pointer">
                <option value="" className="bg-[#0B0B0D]">Tutte le cucine</option>
                {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#0B0B0D]">{c}</option>)}
              </select>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="filter-type"
                className="rounded-full px-3.5 py-1.5 text-sm font-medium bg-white/5 border border-white/10 text-white/80 outline-none hover:border-[#FFC400] cursor-pointer">
                <option value="" className="bg-[#0B0B0D]">Tutti i tipi</option>
                <option value="food" className="bg-[#0B0B0D]">Food</option>
                <option value="farmacia" className="bg-[#0B0B0D]">Farmacia</option>
                <option value="sanitaria" className="bg-[#0B0B0D]">Sanitaria</option>
              </select>
              <button onClick={() => setShowMap((v) => !v)} data-testid="filter-map"
                className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors ${showMap ? "bg-white/10 border-white/20 text-white" : "bg-white/5 border-white/10 text-white/70 hover:border-white/30"}`}>
                <MapIcon size={14} /> {showMap ? "Nascondi mappa" : "Mostra mappa"}
              </button>
            </div>
            {hasDist && (
              <div className="mb-5 max-w-md" data-testid="ch-dist-filter">
                <div className="flex items-center justify-between text-sm text-white/70 mb-2">
                  <span>Distanza massima</span>
                  <span className="font-semibold text-[#FFC400]">{maxDist ? `entro ${maxDist} km` : "Tutte le distanze"}</span>
                </div>
                <input type="range" min="0" max="30" step="1" value={maxDist} onChange={(e) => setMaxDist(Number(e.target.value))}
                  data-testid="dist-slider" className="w-full accent-[#FFC400] cursor-pointer" />
              </div>
            )}
            {showMap && filtered.some((s) => s.lat != null) && (
              <div className="mb-6"><StoreMap stores={filtered} center={loc.includes(",") && !isNaN(parseFloat(loc)) ? parseLatLng(loc) : undefined} /></div>
            )}
          </>
        {loading && <p className="text-white/50" data-testid="ch-loading">Caricamento attività…</p>}
        {err && <p className="text-red-400">{err}</p>}
        {!loading && !err && filtered.length === 0 && (
          <div className="bg-white/[0.04] rounded-2xl border border-white/10 p-8 text-center" data-testid="ch-empty">
            <p className="font-semibold">Nessuna attività trovata</p>
            <p className="text-sm text-white/50 mt-1">Prova a cambiare zona, filtri o termine di ricerca.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s, i) => <StoreCard key={`${s.tenant_slug}-${s.store_slug}`} s={s} i={i} fav={favSet.has(`${s.tenant_slug}/${s.store_slug}`)} onToggleFav={toggleFav} />)}
        </div>
      </section>
      )}

      {mode === "home" && (
        <>
          <section className="max-w-6xl mx-auto px-4 py-10" data-testid="ch-how">
            <h2 className="font-head text-2xl font-bold mb-6 text-center">Come funziona</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {STEPS.map((s, i) => (
                <motion.div key={i} {...(reduce ? {} : { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.4, delay: i * 0.1 } })}
                  className="bg-white/[0.04] rounded-2xl border border-white/10 p-6 hover:border-white/20 transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-[#FFC400]/15 flex items-center justify-center text-[#FFC400]"><s.icon size={22} /></div>
                  <p className="font-head font-bold mt-3">{i + 1}. {s.t}</p>
                  <p className="text-sm text-white/60 mt-1">{s.d}</p>
                </motion.div>
              ))}
            </div>
          </section>

          <section className="max-w-6xl mx-auto px-4 pb-16">
            <div className="rounded-3xl bg-gradient-to-br from-[#FFC400] to-[#c94d1c] text-white p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="font-head text-2xl md:text-3xl font-extrabold">Hai un'attività? Porta gli ordini del tuo locale online.</h3>
                <p className="text-white/85 mt-2 max-w-2xl">Ricevi gli ordini in tempo reale, gestisci il menu da remoto, stampa automaticamente le comande e incassa direttamente sul tuo conto.</p>
              </div>
              <Button onClick={() => window.open("/diventa-partner", "_blank", "noopener,noreferrer")} className="partner-pill rounded-full bg-[#FFC400] hover:bg-[#E6AC00] text-[#0B0B0D] font-bold px-6 shrink-0 relative z-10" data-testid="ch-partner-cta"><span className="partner-spark w-2 h-2 rounded-full bg-[#0B0B0D] mr-2" />Diventa partner Pickfood</Button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
